import { useEffect, useMemo, useState } from "react";
import Sheet from "./Sheet.jsx";
import TabStrip from "./TabStrip.jsx";
import GridStatus from "./GridStatus.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import CompositionBar from "./CompositionBar.jsx";
import SeenChart from "./SeenChart.jsx";
import { formatCompact } from "../lib/renderers.js";
import { agentDistributionFromGroups, osDistributionFromGroups } from "../lib/inventoryDistribution.js";
import { bucketBy, bucketPresence, endpointIntervals, scopedFirstSeenTimestamps, scopedLastSeenTimestamps } from "../lib/seenDistribution.js";
import { inventorySurfacesEnabled } from "../lib/featureFlags.js";

const GRANULARITY_OPTIONS = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
];

// Inventory Surface — the install-footprint sibling of the overview's
// Activity Surface (ActivityChart). Same chrome on purpose: a Sheet with a
// TabStrip of lenses over a framed plot panel + a toggle-chip legend, and
// the same expand-to-fullscreen affordance (data-expand-target + the ⤢
// control; ContentShell lifts the Sheet and hides the surrounding
// data-expand-collapse blocks — see InventoryBody). The body diverges where
// the data forces it to: the Activity Surface is a time-series (events carry
// timestamps); inventory is a current snapshot (latest state per endpoint,
// no history to bucket), so the body is a categorical 100%-stacked
// composition bar rather than a stacked-area chart.
//
// This surface is SERVER-SIDE ONLY — it takes NO client `rows`. Every number
// comes from an analytics:execute aggregate, so it scales to six-figure fleets
// and can't jump/undercount as the table's rows paginate in. If an aggregate is
// unavailable (transient error), the tab shows an explicit empty/error state
// rather than a bounded-but-wrong client rollup. Loading is per-tab.
//
// The composition tabs (AI agents / OS) come from `agentsAgg` / `osAgg`
// (apiClient.getInventory{Agent,Os}Distribution).
//
// The First-seen / Last-seen / Under-management tabs ARE time-bucketed
// (SeenChart), whole-fleet only — hidden entirely once a scope is committed
// (see the scale guard), so they never take an endpoint-id set:
//   • First seen → per-endpoint MIN(change_time) (`firstSeenAgg`). A LOSSY
//     enrollment proxy — captioned as such.
//   • Last seen  → per-endpoint MAX(received_at) over the endpoint status entity
//     (`lastSeenAgg`; needs backend #2162) — a TRUE last-contact, NOT the stale
//     metadata change_time / snapshotTime. Empty until that entity deploys.

const TABS = [
  { id: "agents", label: "AI agents" },
  { id: "os", label: "Operating systems" },
  { id: "firstSeen", label: "First seen" },
  { id: "lastSeen", label: "Last seen" },
  { id: "underMgmt", label: "Under management" },
];

const SEEN_TABS = new Set(["firstSeen", "lastSeen", "underMgmt"]);

// The seen tabs (First seen / Last seen / Under management) show only when the
// inventory flag is on AND at the whole-fleet root scope: they can't scale to a
// committed scope's id list (see the scale guard below), and they're gated off
// by default (O-2696 exposure gate). AI agents / Operating systems are always
// present. Pure + exported so the visibility contract is locked in a test
// without rendering (effects don't run under SSR); the component feeds it the
// live flag + scope.
export function seenTabsAllowed(flagEnabled, scoped) {
  return Boolean(flagEnabled) && !scoped;
}

export function visibleInventoryTabs(flagEnabled, scoped) {
  return seenTabsAllowed(flagEnabled, scoped) ? TABS : TABS.filter((t) => !SEEN_TABS.has(t.id));
}

// Copy for a failed seen-chart fetch, named per tab so an errored fetch can't
// read as the wrong (or a generic) surface. Pure + exported so the mapping is
// locked without rendering.
export function seenErrorLabel(tab) {
  return tab === "firstSeen" ? "first-seen history" : tab === "underMgmt" ? "under-management data" : "last-seen data";
}

// Which fetch error a seen tab surfaces: first-seen and last-seen each carry
// their own; under-management degrades to a point-per-endpoint rather than
// erroring, so it forwards none. Pure + exported so the wiring is locked — this
// is the O-2696 fix (last-seen errors used to be dropped, reading as "no data").
export function seenTabError(tab, firstSeenError, lastSeenError) {
  return tab === "firstSeen" ? firstSeenError : tab === "lastSeen" ? lastSeenError : null;
}

export default function InventorySurface({ agentsAgg = null, osAgg = null, firstSeenAgg = null, firstSeenError = null, lastSeenAgg = null, lastSeenError = null, scoped = false, agentsLoading = false, osLoading = false, firstSeenLoading = false, lastSeenLoading = false, expanded = false, onToggleExpand }) {
  const [tab, setTab] = useState("agents");
  // Bucket granularity for the seen charts, shared across both tabs.
  const [granularity, setGranularity] = useState("month");

  // Scale guard (O-2696). The composition tabs (AI agents / OS) go through
  // buildEndpointAggRoot — a server-side PROMPT→ENDPOINT edge-join with no
  // id-list — so they scope to clusters of any size. The seen tabs (first/last/
  // under-management) can't be scoped that way: first-seen reads the change-log
  // fleet-wide and last-seen's ENDPOINT_STATUS entity is edge-less (root-only),
  // so the only way to narrow them would be a client-side endpoint-id list —
  // which both under-counts past its cap and can't be pushed to the query (an
  // IN-list over ~1024 ids 400s). So the seen tabs are WHOLE-FLEET only: shown
  // at the root scope, hidden once a scope is committed. This surface therefore
  // never takes an id set — just `scoped` (the host's "is a scope committed").
  const flagEnabled = inventorySurfacesEnabled();
  const seenAllowed = seenTabsAllowed(flagEnabled, scoped);
  useEffect(() => {
    if (!seenAllowed && SEEN_TABS.has(tab)) setTab("agents");
  }, [seenAllowed, tab]);

  const agents = useMemo(
    () => (agentsAgg ? agentDistributionFromGroups(agentsAgg.groups, agentsAgg) : null),
    [agentsAgg],
  );
  const os = useMemo(
    () => (osAgg ? osDistributionFromGroups(osAgg.groups) : null),
    [osAgg],
  );
  // Both seen tabs bucket the whole-fleet analytics aggregates by month; they
  // only ever render at the root scope (see the scale guard above), so there's
  // no client-side scope filter and no loaded-rows refinement. First-seen is
  // MIN(change_time) per endpoint, last-seen is MAX(received_at) per endpoint,
  // straight from the aggregates.
  const firstSeenSeries = useMemo(
    () => bucketBy(scopedFirstSeenTimestamps(firstSeenAgg?.byEndpoint), granularity),
    [firstSeenAgg, granularity],
  );
  const lastSeenSeries = useMemo(
    () => bucketBy(scopedLastSeenTimestamps(lastSeenAgg?.byEndpoint), granularity),
    [lastSeenAgg, granularity],
  );
  // "Under management" — occupancy: count each endpoint in every bucket between
  // its first contact (change-log first-seen) and last contact (heartbeat) — the
  // live fleet over time.
  const underMgmtSeries = useMemo(
    () => bucketPresence(endpointIntervals(firstSeenAgg?.byEndpoint, lastSeenAgg?.byEndpoint), granularity),
    [firstSeenAgg, lastSeenAgg, granularity],
  );

  const seenTab = SEEN_TABS.has(tab);
  const seenSeries = tab === "firstSeen" ? firstSeenSeries : tab === "lastSeen" ? lastSeenSeries : underMgmtSeries;
  // dist is only meaningful for the composition tabs; null on the seen tabs.
  const dist = tab === "agents" ? agents : tab === "os" ? os : null;
  // Show the loading state only while the tab's source is still in flight with
  // no result yet; once it resolves (or errs into a fallback) the body renders.
  const loading = seenTab
    ? tab === "firstSeen"
      ? (firstSeenLoading && !firstSeenAgg)
      : tab === "lastSeen"
        // last-seen leads on the analytics aggregate; the loaded rows only refine it,
        // so block until the aggregate resolves (degrading to rows on error).
        ? (lastSeenLoading && !lastSeenAgg && !lastSeenError)
        // underMgmt needs both change-log aggregates (first + last contact); it
        // degrades to a point per endpoint if either errors, so don't block on that.
        : ((lastSeenLoading && !lastSeenAgg && !lastSeenError) || (firstSeenLoading && !firstSeenAgg && !firstSeenError))
    : tab === "agents" ? (agentsLoading && !agentsAgg) : (osLoading && !osAgg);

  // Legend toggle, mirroring ActivityChart's SurfaceChartWithLegend: hidden
  // segments drop out of the bar and the remainder renormalizes. Tracked as
  // the hidden set and reset whenever the segment set changes (tab flip), so
  // a hidden agent never carries into the OS view.
  const allKey = useMemo(() => (dist?.segments ?? []).map((s) => s.key).join("|"), [dist]);
  const [hidden, setHidden] = useState(() => new Set());
  useEffect(() => { setHidden(new Set()); }, [allKey]);
  const toggle = (key) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  const visibleTabs = visibleInventoryTabs(flagEnabled, scoped);
  const tabs = visibleTabs.map((t) => ({
    id: t.id,
    label: t.label,
    active: t.id === tab,
    onSelect: () => setTab(t.id),
  }));

  return (
    <Sheet
      data-expand-target
      diagram
      expandable
      className={expanded ? "h-full" : ""}
      eyebrow="Inventory surface"
      title="Fleet composition"
      actions={
        onToggleExpand ? (
          <button
            type="button"
            onClick={onToggleExpand}
            className="grid h-[24px] w-[24px] place-items-center border border-[color:var(--line)] bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? (
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12l8-8" /><path d="M12 12L4 4" />
              </svg>
            ) : (
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 2h4v4" /><path d="M6 14H2v-4" /><path d="M14 2l-4 4" /><path d="M2 14l4-4" />
              </svg>
            )}
          </button>
        ) : null
      }
    >
      <div className="mt-[6px] flex min-h-0 flex-1 flex-col">
        {/* -mb-[1px] pulls the panel up so the active tab's bottom mask
            (in --plot-bg) covers the panel's top border — same as ActivityChart. */}
        <div className="-mb-[1px]">
          <TabStrip tabs={tabs} />
        </div>
        {loading ? (
          <div className={["rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] text-[color:var(--plot-ink)]", expanded ? "min-h-0 flex-1" : seenTab ? "h-[185px]" : "h-[88px]"].join(" ")}>
            <GridStatus state="loading" label="Loading inventory…" />
          </div>
        ) : seenTab ? (
          <SeenChartBody tab={tab} series={seenSeries} error={seenTabError(tab, firstSeenError, lastSeenError)} granularity={granularity} onGranularity={setGranularity} expanded={expanded} />
        ) : (
          <CompositionBody dist={dist} tab={tab} expanded={expanded} hidden={hidden} onToggle={toggle} onReset={() => setHidden(new Set())} />
        )}
      </div>
    </Sheet>
  );
}

function CompositionBody({ dist, tab, expanded, hidden, onToggle, onReset }) {
  // Server aggregation unavailable (transient error) — there's no client-rows
  // fallback anymore (the surface is server-side only), so say so rather than
  // render an empty bar. Loading is handled by the caller's loading gate.
  if (!dist) {
    return <EmptyPanel expanded={expanded}>Couldn’t load fleet composition</EmptyPanel>;
  }
  // Empty states, tab-aware: no inventoried endpoints in scope vs. a scope
  // whose endpoints simply report no agents read very differently.
  if (dist.endpointsTotal === 0) {
    return <EmptyPanel expanded={expanded}>No inventoried endpoints in this scope</EmptyPanel>;
  }
  if (dist.total === 0) {
    return (
      <EmptyPanel expanded={expanded}>
        {tab === "agents"
          ? `No AI agents reported across ${formatCompact(dist.endpointsTotal)} inventoried endpoints`
          : "No operating-system data for this scope"}
      </EmptyPanel>
    );
  }

  const visible = dist.segments.filter((s) => !hidden.has(s.key));
  const anyHidden = hidden.size > 0;

  return (
    // Single bordered panel — the tab strip notches into its TOP border, so
    // the tabs read as WRAPPING the body (the XRayBody / Activity-Surface
    // effect). A separate border-b-0 bar + border-t-0 legend split left a
    // seam line under the active tab; folding both into one opaque plot-bg
    // box removes it and keeps everything off the Sheet's dotted ground.
    <div className={["rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]", expanded ? "flex min-h-0 flex-1 flex-col" : ""].join(" ")}>
      <div className={["px-[10px] pt-[10px] pb-[9px]", expanded ? "flex min-h-0 flex-1 flex-col" : ""].join(" ")}>
        <div className="text-[11px] leading-[1.18] text-[color:var(--muted)]">
          <strong className="font-medium text-[color:var(--ink)]">{formatCompact(dist.endpointsTotal)}</strong>
          {" endpoints inventoried"}
        </div>
        {/* Canvas bar (CompositionBar) — h-[34px] collapsed, fills when
            expanded so the inline labels have room. */}
        <div className={["mt-[9px]", expanded ? "min-h-0 flex-1" : "h-[34px]"].join(" ")}>
          <CompositionBar segments={visible} />
        </div>
      </div>

      {/* Legend — inside the panel, divided from the bar by a soft hairline.
          Ported from ActivityChart's SurfaceChartWithLegend: colored dot +
          label (line-through when hidden) + count, with an "All" reset. Each
          chip carries a HoverTooltip with its full count + share. */}
      <div className="flex flex-wrap items-center gap-x-[2px] gap-y-[1px] px-[6px] py-[4px]">
        {dist.segments.map((s) => {
          const on = !hidden.has(s.key);
          return (
            <HoverTooltip
              key={s.key}
              placement="top"
              label={
                <div className="grid min-w-[150px] gap-[3px]">
                  <div className="flex items-center gap-[6px]">
                    <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: s.color }} aria-hidden="true" />
                    <span className="font-medium text-[color:var(--ink)]">{s.label}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-[12px]">
                    <span className="text-[color:var(--muted)]">Endpoints</span>
                    <span className="tabular-nums text-[color:var(--ink)]">{formatCompact(s.count)}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-[12px]">
                    <span className="text-[color:var(--muted)]">{tab === "agents" ? "Share of installs" : "Share of fleet"}</span>
                    <span className="tabular-nums text-[color:var(--ink)]">{Math.round(s.share * 100)}%</span>
                  </div>
                  <div className="text-[color:var(--muted-soft)]">{on ? "Click to hide from the bar" : "Click to show in the bar"}</div>
                </div>
              }
            >
              <button
                type="button"
                onClick={() => onToggle(s.key)}
                aria-label={on ? `Hide ${s.label}` : `Show ${s.label}`}
                className={[
                  "inline-flex items-center gap-[3px] rounded-[2px] border border-transparent px-[5px] py-[2px] text-[9px] leading-[1.08] transition-colors cursor-pointer",
                  on
                    ? "text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                    : "text-[color:var(--muted-soft)] line-through opacity-50 hover:opacity-100 hover:text-[color:var(--muted)]",
                ].join(" ")}
              >
                <span
                  className={["h-[5px] w-[5px] rounded-[999px]", on ? "" : "ring-1 ring-inset ring-[color:var(--line-strong)]"].join(" ")}
                  style={{ backgroundColor: on ? s.color : "transparent" }}
                  aria-hidden="true"
                />
                {s.label}
                <span className="tabular-nums text-[color:var(--muted-soft)]">{formatCompact(s.count)}</span>
              </button>
            </HoverTooltip>
          );
        })}
        {anyHidden ? (
          <button
            type="button"
            onClick={onReset}
            aria-label="Show all segments"
            className="ml-[2px] inline-flex items-center rounded-[2px] border border-[color:var(--line)] px-[5px] py-[2px] text-[9px] leading-[1.08] tracking-[0.06em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
          >
            All
          </button>
        ) : null}
      </div>
    </div>
  );
}

// The time-bucketed seen tabs — first-seen rollout (bars + cumulative line),
// last-seen recency, and under-management occupancy (live fleet over time).
// Same single bordered plot-bg panel as CompositionBody (the tab strip notches
// its top border), but a SeenChart instead of the composition bar, a
// granularity switch, and a coverage/meaning caption instead of the legend.
function SeenChartBody({ tab, series, error = null, granularity = "month", onGranularity, expanded }) {
  const isFirst = tab === "firstSeen";
  const isMgmt = tab === "underMgmt";
  // Surface a failed query explicitly — an errored fetch must not read as
  // "no data" (an empty chart hid the 90-day range-cap rejection once already).
  if (error) {
    const label = seenErrorLabel(tab);
    return (
      <EmptyPanel expanded={expanded}>
        Couldn’t load {label}: {error.message || String(error)}
      </EmptyPanel>
    );
  }
  if (!series.buckets.length) {
    return (
      <EmptyPanel expanded={expanded}>
        {isFirst
          ? "No first-seen history for these endpoints yet"
          : isMgmt
            ? "No endpoints to chart for this scope"
            : "No last-seen data for these endpoints"}
      </EmptyPanel>
    );
  }
  const covered = series.total;

  return (
    <div className={["rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]", expanded ? "flex min-h-0 flex-1 flex-col" : ""].join(" ")}>
      <div className={["px-[10px] pt-[10px] pb-[9px]", expanded ? "flex min-h-0 flex-1 flex-col" : ""].join(" ")}>
        <div className="flex items-start justify-between gap-[10px]">
          <div className="text-[11px] leading-[1.18] text-[color:var(--muted)]">
            {isFirst ? (
              <>
                <strong className="font-medium text-[color:var(--ink)]">{formatCompact(covered)}</strong>
                {" endpoints with a metadata change"}
              </>
            ) : (
              <>
                <strong className="font-medium text-[color:var(--ink)]">{formatCompact(covered)}</strong>
                {isMgmt ? " endpoints under management" : " endpoints seen in the last 30 days"}
              </>
            )}
          </div>
          {/* Granularity switch (Day / Week / Month) — shared across the seen tabs. */}
          <div className="flex shrink-0 items-center gap-[2px]">
            {GRANULARITY_OPTIONS.map((opt) => {
              const on = opt.id === granularity;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onGranularity?.(opt.id)}
                  aria-pressed={on}
                  className={[
                    "rounded-[2px] border px-[6px] py-[2px] text-[9px] leading-[1.08] tracking-[0.06em] uppercase transition-colors cursor-pointer",
                    on
                      ? "border-[color:var(--line-strong)] bg-[color:var(--control-bg)] text-[color:var(--ink)]"
                      : "border-transparent text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                  ].join(" ")}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className={["mt-[9px]", expanded ? "min-h-0 flex-1" : "h-[150px]"].join(" ")}>
          <SeenChart buckets={series.buckets} granularity={series.granularity} showCumulative={isFirst} valueLabel={isFirst ? "New" : isMgmt ? "On fleet" : "Last seen"} />
        </div>
        <div className="mt-[7px] text-[9px] leading-[1.3] text-[color:var(--muted-soft)]">
          {isFirst
            ? `Bars show endpoints with a metadata change each ${granularity}. The line is cumulative (${formatCompact(series.maxCumulative)}). This change log excludes endpoints with no metadata changes after enrollment, so totals can be lower than inventory.`
            : isMgmt
              ? `Endpoints active between first and last contact each ${granularity}. First contact comes from the incomplete metadata change log. Last contact comes from heartbeats retained for 30 days. Endpoints without first-contact data count only at last contact.`
              : `Most recent heartbeat per endpoint by ${granularity}, within the 30-day retention window. Endpoints silent for longer are absent, so totals can be lower than First seen.`}
        </div>
      </div>
    </div>
  );
}

function EmptyPanel({ children, expanded = false }) {
  return (
    <div className={["grid place-items-center rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[12px] text-center text-[12px] leading-[1.3] text-[color:var(--muted)]", expanded ? "min-h-0 flex-1" : "h-[88px]"].join(" ")}>
      {children}
    </div>
  );
}
